import { createHttpClient } from '@adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CoverageZonesApiAdapter } from '../adapters/CoverageZonesApiAdapter';
import { CreateCoverageRulePayload, ReorderPayload, UpdateCoverageRulePayload } from '../models/CoverageRule';
import { Service } from '../services/Service';

export const useCreateCoverageRule = () => {
    const { toast } = useToast();
    const { t } = useTranslation('coverageZonesModule');
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new CoverageZonesApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    return useMutation({
        mutationFn: ({ serviceId, payload }: { serviceId: number; payload: CreateCoverageRulePayload }) =>
            service.createCoverageRule(serviceId, payload),
        onSuccess: async (_, variables) => {
            // Invalidar y refetch inmediatamente
            await queryClient.invalidateQueries({
                queryKey: ['coverage-rules', variables.serviceId],
                refetchType: 'active'
            });
            // Forzar refetch
            await queryClient.refetchQueries({
                queryKey: ['coverage-rules', variables.serviceId]
            });
            toast({
                title: t('toast.createSuccess'),
            });
        },
        onError: (error: any) => {
            const apiMsg = error?.response?.data?.message || error?.message || t('toast.createError');
            toast({
                title: t('toast.createError'),
                description: apiMsg,
                variant: 'destructive',
            });
        },
    });
};

export const useUpdateCoverageRule = () => {
    const { toast } = useToast();
    const { t } = useTranslation('coverageZonesModule');
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new CoverageZonesApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    return useMutation({
        mutationFn: ({ ruleId, payload, serviceId }: { ruleId: number; payload: UpdateCoverageRulePayload; serviceId: number }) =>
            service.updateCoverageRule(ruleId, payload),
        onSuccess: async (_, variables) => {
            // Invalidar y refetch inmediatamente
            await queryClient.invalidateQueries({
                queryKey: ['coverage-rules', variables.serviceId],
                refetchType: 'active'
            });
            // Forzar refetch
            await queryClient.refetchQueries({
                queryKey: ['coverage-rules', variables.serviceId]
            });
            toast({
                title: t('toast.updateSuccess'),
            });
        },
        onError: (error: any) => {
            const apiMsg = error?.response?.data?.message || error?.message || t('toast.updateError');
            toast({
                title: t('toast.updateError'),
                description: apiMsg,
                variant: 'destructive',
            });
        },
    });
};

export const useDeleteCoverageRule = () => {
    const { toast } = useToast();
    const { t } = useTranslation('coverageZonesModule');
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new CoverageZonesApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    return useMutation({
        mutationFn: ({ ruleId, serviceId }: { ruleId: number; serviceId: number }) =>
            service.deleteCoverageRule(ruleId),
        onSuccess: async (_, variables) => {
            // Invalidar y refetch inmediatamente
            await queryClient.invalidateQueries({
                queryKey: ['coverage-rules', variables.serviceId],
                refetchType: 'active'
            });
            // Forzar refetch
            await queryClient.refetchQueries({
                queryKey: ['coverage-rules', variables.serviceId]
            });
            toast({
                title: t('toast.deleteSuccess'),
            });
        },
        onError: (error: any) => {
            const apiMsg = error?.response?.data?.message || error?.message || t('toast.deleteError');
            toast({
                title: t('toast.deleteError'),
                description: apiMsg,
                variant: 'destructive',
            });
        },
    });
};

export const useReorderCoverageRules = () => {
    const { toast } = useToast();
    const { t } = useTranslation('coverageZonesModule');
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new CoverageZonesApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    return useMutation({
        mutationFn: ({ serviceId, payload }: { serviceId: number; payload: ReorderPayload }) =>
            service.reorderCoverageRules(serviceId, payload),
        onSuccess: async (_, variables) => {
            // Invalidar y refetch inmediatamente
            await queryClient.invalidateQueries({
                queryKey: ['coverage-rules', variables.serviceId],
                refetchType: 'active'
            });
            // Forzar refetch
            await queryClient.refetchQueries({
                queryKey: ['coverage-rules', variables.serviceId]
            });
            toast({
                title: t('toast.reorderSuccess'),
            });
        },
        onError: (error: any) => {
            const apiMsg = error?.response?.data?.message || error?.message || t('toast.reorderError');
            toast({
                title: t('toast.reorderError'),
                description: apiMsg,
                variant: 'destructive',
            });
        },
    });
};

export const useBulkImportCoverageRules = () => {
    const { toast } = useToast();
    const { t } = useTranslation('coverageZonesModule');
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new CoverageZonesApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    return useMutation({
        mutationFn: ({
            serviceId,
            file,
            countryId,
            typeRule,
            paymentMethodId,
            notes,
            groupRanges,
        }: {
            serviceId: number;
            file: File;
            countryId: number;
            typeRule: 'allow' | 'deny';
            paymentMethodId?: number | null;
            notes?: string | null;
            groupRanges?: boolean;
        }) =>
            service.bulkImportCoverageRules(
                serviceId,
                file,
                countryId,
                typeRule,
                paymentMethodId,
                notes,
                groupRanges
            ),
        onSuccess: async (_, variables) => {
            // Invalidar y refetch inmediatamente
            await queryClient.invalidateQueries({
                queryKey: ['coverage-rules', variables.serviceId],
                refetchType: 'active'
            });
            // Forzar refetch
            await queryClient.refetchQueries({
                queryKey: ['coverage-rules', variables.serviceId]
            });
            toast({
                title: t('toast.bulkImportSuccess'),
            });
        },
        onError: (error: any) => {
            const apiMsg = error?.response?.data?.message || error?.message || t('toast.bulkImportError');
            toast({
                title: t('toast.bulkImportError'),
                description: apiMsg,
                variant: 'destructive',
            });
        },
    });
};

export const useExportCoverageRules = () => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new CoverageZonesApiAdapter(httpClient);
    const service = new Service(apiAdapter);

    return useMutation<string, Error, { countryId: number; courierServiceId?: number }>({
        mutationFn: ({
            countryId,
            courierServiceId,
        }: {
            countryId: number;
            courierServiceId?: number;
        }) => service.exportCoverageRules(countryId, courierServiceId),
    });
};

